import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartAggregation, ChartType, ChartValueFormat, CreateChartData, ListViewChart, UpdateChartData } from "@/services/list-view";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

type FieldOption = { value: string; label: string; type?: string };

const NUMERIC_TYPES = new Set(["integer", "float", "decimal"]);

// Validation schema -- enforces the PR-review checklist invariants:
//   - `name` and `group_by_field` cannot be empty
//   - `aggregate_field` is required when aggregation is `sum` or `avg`
// Using react-hook-form + zod is the convention mandated by CLAUDE.md.
const chartSchema = z
    .object({
        name: z.string().trim().min(1, "Name is required"),
        chart_type: z.enum(["bar", "horizontal_bar", "line", "pie"]),
        aggregation: z.enum(["count", "sum", "avg"]),
        group_by_field: z.string().min(1, "Group by field is required"),
        aggregate_field: z.string().nullable().optional(),
        value_format: z.enum(["number", "currency", "percentage"])
    })
    .superRefine((data, ctx) => {
        if ((data.aggregation === "sum" || data.aggregation === "avg") && !data.aggregate_field) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Required for sum and average",
                path: ["aggregate_field"]
            });
        }
    });

type ChartFormValues = z.infer<typeof chartSchema>;

type ChartConfigModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialChart?: ListViewChart | null;
    fieldOptions: FieldOption[];
    onSubmit: (data: CreateChartData | UpdateChartData) => Promise<void> | void;
};

const CHART_TYPES: { value: ChartType; label: string }[] = [
    { value: "bar", label: "Bar (vertical)" },
    { value: "horizontal_bar", label: "Bar (horizontal)" },
    { value: "line", label: "Line" },
    { value: "pie", label: "Pie" }
];

const AGGREGATIONS: { value: ChartAggregation; label: string }[] = [
    { value: "count", label: "Count" },
    { value: "sum", label: "Sum" },
    { value: "avg", label: "Average" }
];

const VALUE_FORMATS: { value: ChartValueFormat; label: string; hint: string }[] = [
    { value: "number", label: "Number", hint: "e.g. 54 785,60" },
    { value: "currency", label: "Currency (€)", hint: "e.g. 54 785,60 €" },
    { value: "percentage", label: "Percentage (%)", hint: "Treats values as a 0–1 ratio (0.84 → 84 %)" }
];

// Smart default: infer the most likely value format from a field name.
function inferValueFormat(fieldName: string | null | undefined): ChartValueFormat {
    if (!fieldName) return "number";
    const lower = fieldName.toLowerCase();
    if (/ratio|progress|percent/.test(lower)) return "percentage";
    if (/revenue|turnover|funding|amount|price|cost|cogs|tarif|net_revenue|organism_part|personal_funding/.test(lower)) return "currency";
    return "number";
}

const EMPTY_DEFAULTS: ChartFormValues = {
    name: "",
    chart_type: "bar",
    aggregation: "count",
    aggregate_field: "",
    group_by_field: "",
    value_format: "number"
};

export function ChartConfigModal({ open, onOpenChange, initialChart, fieldOptions, onSubmit }: ChartConfigModalProps) {
    const [submitError, setSubmitError] = useState<string | null>(null);
    // The user's choice for value_format is sticky: once they pick one manually,
    // we stop overriding it via the smart auto-detect on aggregate_field changes.
    const valueFormatTouchedRef = useRef(false);

    const form = useForm<ChartFormValues>({
        resolver: zodResolver(chartSchema),
        defaultValues: EMPTY_DEFAULTS,
        mode: "onSubmit"
    });

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors, isSubmitting }
    } = form;

    const aggregation = watch("aggregation");
    const aggregateField = watch("aggregate_field");
    const valueFormat = watch("value_format");

    const requiresAggregateField = aggregation === "sum" || aggregation === "avg";

    // Reset form when the modal opens (or initialChart changes while open).
    useEffect(() => {
        if (!open) return;
        setSubmitError(null);
        valueFormatTouchedRef.current = false;
        if (initialChart) {
            reset({
                name: initialChart.name,
                chart_type: initialChart.chart_type,
                aggregation: initialChart.aggregation,
                aggregate_field: initialChart.aggregate_field ?? "",
                group_by_field: initialChart.group_by_field,
                value_format: initialChart.value_format ?? "number"
            });
        } else {
            reset(EMPTY_DEFAULTS);
        }
    }, [open, initialChart, reset]);

    // Auto-detect value_format from the chosen aggregate field, unless the
    // user has already overridden it manually.
    useEffect(() => {
        if (valueFormatTouchedRef.current) return;
        if (aggregation === "count") {
            setValue("value_format", "number");
            return;
        }
        if (aggregateField) {
            setValue("value_format", inferValueFormat(aggregateField));
        }
    }, [aggregateField, aggregation, setValue]);

    const onValidSubmit = handleSubmit(async data => {
        setSubmitError(null);
        try {
            const payload: CreateChartData = {
                name: data.name.trim(),
                chart_type: data.chart_type,
                aggregation: data.aggregation,
                group_by_field: data.group_by_field,
                aggregate_field: requiresAggregateField ? data.aggregate_field || null : null,
                value_format: data.aggregation === "count" ? "number" : data.value_format
            };
            await onSubmit(payload);
            onOpenChange(false);
        } catch (err: any) {
            setSubmitError(err?.message || "Failed to save the chart");
        }
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{initialChart ? "Edit chart" : "Add a chart"}</DialogTitle>
                    <DialogDescription>Configure how data from this list view is aggregated and displayed.</DialogDescription>
                </DialogHeader>
                <form onSubmit={onValidSubmit} noValidate>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="chart-name">Name</Label>
                            <Input
                                id="chart-name"
                                placeholder="e.g. Revenue per status"
                                disabled={isSubmitting}
                                aria-invalid={errors.name ? "true" : undefined}
                                {...register("name")}
                            />
                            {errors.name?.message && <p className="text-xs text-red-600">{errors.name.message}</p>}
                        </div>

                        <div className="grid gap-2">
                            <Label>Chart type</Label>
                            <Controller
                                control={control}
                                name="chart_type"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CHART_TYPES.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label>Aggregation</Label>
                            <Controller
                                control={control}
                                name="aggregation"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {AGGREGATIONS.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        {requiresAggregateField &&
                            (() => {
                                const numericOptions = fieldOptions.filter(opt => opt.type && NUMERIC_TYPES.has(opt.type));
                                return (
                                    <div className="grid gap-2">
                                        <Label>Field to aggregate</Label>
                                        {numericOptions.length === 0 ? (
                                            <p className="text-sm text-gray-500">No numeric field available on this object — only Count aggregation is supported here.</p>
                                        ) : (
                                            <Controller
                                                control={control}
                                                name="aggregate_field"
                                                render={({ field }) => (
                                                    <Select value={field.value || ""} onValueChange={field.onChange} disabled={isSubmitting}>
                                                        <SelectTrigger aria-invalid={errors.aggregate_field ? "true" : undefined}>
                                                            <SelectValue placeholder="Choose a numeric field" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {numericOptions.map(opt => (
                                                                <SelectItem key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                        )}
                                        {errors.aggregate_field?.message && <p className="text-xs text-red-600">{errors.aggregate_field.message}</p>}
                                        <p className="text-xs text-gray-500">Only numeric fields (integer, float, decimal) are eligible for Sum and Average.</p>
                                    </div>
                                );
                            })()}

                        <div className="grid gap-2">
                            <Label>Group by</Label>
                            <Controller
                                control={control}
                                name="group_by_field"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                                        <SelectTrigger aria-invalid={errors.group_by_field ? "true" : undefined}>
                                            <SelectValue placeholder="Choose a field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {fieldOptions.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.group_by_field?.message && <p className="text-xs text-red-600">{errors.group_by_field.message}</p>}
                        </div>

                        {aggregation !== "count" && (
                            <div className="grid gap-2">
                                <Label>Value format</Label>
                                <Controller
                                    control={control}
                                    name="value_format"
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={v => {
                                                valueFormatTouchedRef.current = true;
                                                field.onChange(v);
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {VALUE_FORMATS.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                <p className="text-xs text-gray-500">{VALUE_FORMATS.find(f => f.value === valueFormat)?.hint}</p>
                            </div>
                        )}

                        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "Saving..." : initialChart ? "Save changes" : "Add chart"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
